import { I18n } from "react-redux-i18n";
import React from "react";

import {
  FieldQueryColumn,
  FieldQueryColumnComponentRenderParams,
  FieldQueryItem,
} from "@common/components/field-query/field-query.types";
import { FieldPrimaryListDto } from "@common/dto/field/list-primary-dto";
import { Flex } from "@common/components/Flex";
import { Ellipsis } from "@common/components/Ellipsis";
import { Button } from "@common/components/Button";
import { getTestAttributes } from "@common/utils/tests";
import Dropdown from "@common/containers/fields/Dropdown";

import {
  SpreadsheetManagementSortDirection,
  SpreadsheetManagementSortTableField,
} from "./spreadsheet-management.types";
import { SPREADSHEET_MODULE_NAME } from "../../constants/shared";
import { MessageConfirmParams } from "@common/hooks/message";

type SortTableControllerProps = {
  showConfirmMessage: (props: MessageConfirmParams) => void;
};

export const useSortTableController = ({ showConfirmMessage }: SortTableControllerProps) => {
  const [sortTableFields, setSortTableFields] = React.useState<Array<SpreadsheetManagementSortTableField>>([]);

  const columnActions = React.useMemo(
    () => [
      {
        id: getTestAttributes(SPREADSHEET_MODULE_NAME, "field-query-sort-table-cancel", "button").id,
        iconSrc: "gif/cancel.gif",
        tooltip: I18n.t("Remove Field"),
        onClick: (removedInternalFieldId: FieldQueryItem["id"]) => {
          setSortTableFields((currentFields) => currentFields.filter((field) => field.id !== removedInternalFieldId));
        },
      },
    ],
    [sortTableFields]
  );

  const directionOptions = React.useMemo(
    () => [
      {
        value: SpreadsheetManagementSortDirection.ascending,
        label: I18n.t("Ascending"),
      },
      {
        value: SpreadsheetManagementSortDirection.descending,
        label: I18n.t("Descending"),
      },
    ],
    []
  );

  const handleFieldDirectionChange = React.useCallback(
    (direction: SpreadsheetManagementSortDirection, row: FieldQueryItem<SpreadsheetManagementSortTableField>) => {
      const fields = sortTableFields.map((field) => {
        if (field.id === row.id) {
          return {
            ...field,
            direction,
          };
        }

        return field;
      });
      setSortTableFields(fields);
    },
    [sortTableFields]
  );

  const columnsSortTable: Array<FieldQueryColumn> = React.useMemo(
    () => [
      {
        id: "index",
        component: ({ rowIndex }: FieldQueryColumnComponentRenderParams<FieldPrimaryListDto>) => {
          return rowIndex + 1;
        },
      },
      {
        label: I18n.t("Type"),
        id: "type",
        component: ({ row }: FieldQueryColumnComponentRenderParams<{ field: FieldPrimaryListDto }>) => {
          return <img src={row.field.imageURL} alt={row.field.imageURL} />;
        },
      },
      {
        label: I18n.t("Field"),
        id: "field",
        component: ({ row }: FieldQueryColumnComponentRenderParams<{ field: FieldPrimaryListDto }>) => {
          return (
            <Flex alignItems="center" justify="space-between">
              <Ellipsis
                maxWidth={300}
                text={row.field.name}
                className="SpreadsheetDetailsSpreadsheetManagement__content__management__builder__column__title"
              />
              <Flex className="SpreadsheetDetailsSpreadsheetManagement__content__management__builder__column__actions">
                {columnActions.map((action) => (
                  <Button
                    id={action.id}
                    key={action.id}
                    className="SpreadsheetDetailsSpreadsheetManagement__content__management__builder__column__action"
                    onClick={() => action.onClick(row.id)}
                    variant="text"
                    title={action.tooltip}
                    aria-label={action.tooltip}
                  >
                    <img src={action.iconSrc} height="100%" alt={action.tooltip} />
                  </Button>
                ))}
              </Flex>
            </Flex>
          );
        },
      },
      {
        label: I18n.t("Direction"),
        id: "direction",
        component: ({ rowIndex, row }: FieldQueryColumnComponentRenderParams<SpreadsheetManagementSortTableField>) => {
          return (
            <Dropdown
              {...getTestAttributes(SPREADSHEET_MODULE_NAME, `field-query-sort-options-${rowIndex}`, "dropdown")}
              placeholder={I18n.t("Select")}
              className="SkipLogic__dropdown"
              searchable={false}
              selectedValue={row.direction}
              onValueChanged={(val: SpreadsheetManagementSortDirection) => {
                handleFieldDirectionChange(val, row);
              }}
              options={directionOptions}
            />
          );
        },
      },
    ],
    [columnActions, directionOptions, handleFieldDirectionChange]
  );

  const handleSortTableFieldsRemove = React.useCallback(() => {
    showConfirmMessage({
      cancelText: I18n.t("No"),
      confirmText: I18n.t("Yes"),
      confirmBtnId: "btn-message-box-ok",
      cancelBtnId: "btn-message-box-dont-cancel",
      content: I18n.t("Are you sure you wish to remove all sort fields?"),
      onCustomOk: () => {
        setSortTableFields([]);
      },
    });
  }, [showConfirmMessage]);

  return {
    columnsSortTable,
    sortTableFields,
    setSortTableFields,
    handleSortTableFieldsRemove,
  };
};
